import {
  Box,
  Grid,
  Typography,
  IconButton,
  TextField,
  MenuItem,
  Select,
} from "@mui/material";
import React, { useState, useRef, useEffect } from "react";
import EditIcon from "@mui/icons-material/Edit";
import CheckIcon from "@mui/icons-material/Check";
import { openPopup } from "../../stateManagement/actions/popupActions";
import { formatAmount } from "./manageRequestsService";

interface CreditLimitData {
  creditType: string;
  amount: string;
  days: string;
  invoice: string;
}

interface Props {
  data?: CreditLimitData;
  onAutoSave?: (updatedData: CreditLimitData) => void;
  onDataChange?: (updateddata: CreditLimitData) => void;
  status: string | undefined;
  outletType: string;
}

const CreditLimitRequest: React.FC<Props> = ({ data, onAutoSave, onDataChange, status, outletType }) => {
  const [editMode, setEditMode] = useState(false);
  const [formData, setFormData] = useState<CreditLimitData>({
    creditType: data?.creditType || "TEMPORARY",
    amount: data?.amount || "",
    days: data?.days || "",
    invoice: data?.invoice || "",
  });

  const containerRef = useRef<HTMLDivElement>(null);

  const handleChange = (field: keyof CreditLimitData, value: string) => {
    let validatedValue = value;

    if (field === "amount") {
      if (value && !/^\d*\.?\d*$/.test(value)) {
        return;
      }
    } else if (field === "days" || field === "invoice") {
      if (value && !/^\d+$/.test(value)) {
        return;
      }
    }

    if (
      (formData[field] === "0" || formData[field] === "") &&
      validatedValue.length > 1 &&
      validatedValue.startsWith("0")
    ) {
      validatedValue = validatedValue.replace(/^0+/, "");
    }

    if (formData[field] === "0" && validatedValue !== "0") {
      validatedValue = validatedValue.replace(/^0+/, "");
    }

    if (validatedValue === "") validatedValue = "0";

    const updated = { ...formData, [field]: validatedValue };
    setFormData(updated);
    onAutoSave?.(updated);
    onDataChange?.(updated);
  };

  // close edit mode when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node) &&
        !(event.target as HTMLElement).closest('.MuiPopover-root')
      ) {
        setEditMode(false);
      }
    };

    if (editMode) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [editMode]);

  const boxStyle = {
    px: 2,
    bgcolor: "white",
    // minHeight: "90px",
    display: "flex",
    flexDirection: "column" as const,
    alignItems: "flex-start",
    justifyContent: "flex-start",
    pb: 2,
    fontSize: "12px",
    position: "relative" as const,
  };

  const fieldBox = {
    mt: 0.2,
    bgcolor: "white",
    px: 1.5,
    py: 0.5,
    borderRadius: 2,
    fontWeight: 500,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "flex-start",
    minWidth: "150px",
    border: editMode ? "1px solid #bfbfc0" : "0.5px solid #bfbfc0",
    transition: "border-color 0.2s",
  };

  return (
    <Box sx={boxStyle} ref={containerRef}>
      {status && status.toLowerCase() !== "rejected" && status.toLowerCase() !== "approved" && status.toLowerCase() !== "rfc_rejected" && status.toLowerCase() !== "rfc_approved" && <IconButton
        onClick={() => setEditMode(!editMode)}
        sx={{
          position: "absolute",
          top: 5,
          right: 5,
          width: 26,
          height: 26,
          color: editMode ? "#2e7d32" : "#00897b", 
          bgcolor: "rgba(0, 137, 123, 0.08)", 
          borderRadius: "50%",
          transition: "all 0.2s ease",
          "& svg": {
            fontSize: 14,
          },
          "&:hover": {
            bgcolor: "rgba(0, 137, 123, 0.15)",
            transform: "scale(1.05)",
          },
          "&:active": {
            bgcolor: "rgba(0, 137, 123, 0.25)",
            transform: "scale(0.95)",
          },
        }}
      >
        {editMode ? <CheckIcon /> : <EditIcon />}
      </IconButton>}


      <Grid container spacing={0.5} direction="column">
        {/* Increase Type */}
        <Grid item>
          <Typography
            variant="subtitle2"
            color="#bfbfc0"
            sx={{ fontSize: "10px" }}
          >
            Credit limit Increase by
          </Typography>

          {(editMode && ((outletType && outletType != "TCS") || !outletType)) ? (
            <Select
              value={formData.creditType}
              onChange={(e) => handleChange("creditType", e.target.value)}
              variant="standard"
              disableUnderline
              sx={{
                ...fieldBox,
                minWidth: "150px",
                height: "auto",
                px: 1.5,
                py: 0.5,
                fontSize: 12,
                fontWeight: 500,
                "& .MuiSelect-select": {
                  p: 0,
                  m: 0,
                  display: "flex",
                  alignItems: "center",
                },
                "& .MuiSelect-icon": {
                  right: 6,
                  top: "calc(50% - 0.4em)",
                },
                "& fieldset": { border: "none" },
                border: editMode ? "1px solid #bfbfc0" : "0.5px solid #bfbfc0",
                borderRadius: 2,
                bgcolor: "white",
                transition: "border-color 0.2s",
              }}
            >
              <MenuItem value="TEMPORARY" sx={{ fontSize: 12, py: 0.5 }}>
                TEMPORARY
              </MenuItem>
              <MenuItem value="PERMANENT" sx={{ fontSize: 12, py: 0.5 }}>
                PERMANENT
              </MenuItem>
            </Select>
          ) : (
            <Box sx={fieldBox}>{formData.creditType}</Box>
          )}

        </Grid>

        {/* Other Fields */}
        <Grid item>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            {[
              { label: "Amount", field: "amount", bg: "#FFF2F2" },
              { label: "Days", field: "days", bg: "#E9F2FF" },
              { label: "Invoice", field: "invoice", bg: "#EAF9E9" },
            ].map((item, i) => (
              <Box
                key={i}
                sx={{
                  ...fieldBox,
                  bgcolor: item.bg,
                  cursor: editMode ? "text" : "default",
                  border: editMode ? "1px solid #bfbfc0" : (item.bg == "white" ? "0.5px solid #bfbfc0" : "none")
                }}
              >
                <Typography sx={{ fontSize: 12, fontWeight: 500, mr: 0.5 }}>
                  {item.label}:
                </Typography>

                {editMode ? (
                  <input
                    type="text"
                    value={formData[item.field as keyof CreditLimitData]}
                    onChange={(e) =>
                      handleChange(item.field as keyof CreditLimitData, e.target.value)
                    }
                    style={{
                      border: "none",
                      background: "transparent",
                      outline: "none",
                      width: "60px",
                      fontSize: "12px",
                      fontWeight: 500,
                      color: "#333",
                    }}
                  />
                ) : (
                  <Typography sx={{ fontSize: 12 }}>
                    {item.field === "amount" ? formatAmount(formData[item.field as keyof CreditLimitData] || "0"): formData[item.field as keyof CreditLimitData] || "0"}
                  </Typography>
                )}
              </Box>
            ))}
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export default CreditLimitRequest;

